import type { NavListProps } from '../types';

import { isActiveLink, isExternalLink } from 'minimal-shared/utils';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { useTranslate } from '@/core/locales';
import { TRANSLATION_NAMESPACES } from '@/core/constants';

import { Iconify } from '@/components/iconify';
import { Box, Drawer, Typography } from '@/components/ui';

import { usePathname } from '@/routes/hooks';

import { NavLi } from '../components';
import { NavItem } from './nav-mobile-item';

// Helpers
const normalizePath = (p?: string) => (p ? p.replace(/\/+$/, '') : '');
const getItemPath = (item: any) => item?.category?.path ?? item?.path;

const isInsideTree = (pathname: string, base: string) => {
  const cur = normalizePath(pathname);
  const b = normalizePath(base);
  return cur === b || cur.startsWith(`${b}/`);
};

const isStrictDescendantOf = (pathname: string, base: string) => {
  const cur = normalizePath(pathname);
  const b = normalizePath(base);
  return cur.startsWith(`${b}/`);
};

// ----------------------------------------------------------------------

type LevelItem = {
  id: number;
  menu: any;
  open: boolean;
  mountInstant?: boolean;
};

export function NavList({ data, className, ...other }: NavListProps) {
  const pathname = usePathname();
  const { t } = useTranslate(TRANSLATION_NAMESPACES.NAVIGATION);

  const hasChildren = useMemo(() => !!data.children?.length, [data.children]);
  const isActive = useMemo(
    () => isActiveLink(pathname, data.category.path, data.category.deepMatch ?? hasChildren),
    [pathname, data.category.path, data.category.deepMatch, hasChildren]
  );

  const [levels, setLevels] = useState<LevelItem[]>([]);
  const levelIdRef = useRef(0);
  const nextLevelId = () => ++levelIdRef.current;

  const openFirstLevel = useCallback(
    (e?: React.MouseEvent) => {
      if (!hasChildren) return;
      e?.preventDefault();
      e?.stopPropagation();
      setLevels([{ id: nextLevelId(), menu: data, open: true, mountInstant: false }]);
    },
    [data, hasChildren]
  );

  const pushLevel = useCallback((menu: any, parentIndex: number) => {
    setLevels((prev) => {
      const next = prev.slice(0, parentIndex + 1);
      next.push({ id: nextLevelId(), menu, open: true, mountInstant: false });
      return next;
    });
  }, []);

  const closeFromIndex = useCallback((index: number) => {
    setLevels((prev) => prev.map((it, i) => (i >= index ? { ...it, open: false } : it)));
  }, []);

  const cleanupTrailingClosed = useCallback(() => {
    setLevels((prev) => {
      let cut = prev.length;
      while (cut > 0 && prev[cut - 1].open === false) cut -= 1;
      return cut === prev.length ? prev : prev.slice(0, cut);
    });
  }, []);

  const closeAll = useCallback(() => {
    setLevels((prev) => (prev.length ? prev.map((it) => ({ ...it, open: false })) : prev));
  }, []);

  const computeOpenChain = useCallback(
    (rootMenu: any): any[] => {
      const chain: any[] = [];
      let current: any = rootMenu;

      const rootPath = getItemPath(current);
      if (!current?.children?.length || !rootPath) return chain;

      // Only operate within this root tree
      if (!isInsideTree(pathname, rootPath)) return chain;

      while (current && Array.isArray(current.children) && current.children.length) {
        chain.push(current);

        // Find the child whose subtree contains the current path
        const next = current.children.find((child: any) => {
          const childPath = getItemPath(child);
          return childPath && isInsideTree(pathname, childPath);
        });

        if (!next) break;

        const nextPath = getItemPath(next)!;
        const nextHasChildren = Array.isArray(next.children) && next.children.length > 0;

        if (nextHasChildren && isStrictDescendantOf(pathname, nextPath)) {
          current = next;
          continue;
        }

        break;
      }

      return chain;
    },
    [pathname]
  );

  useEffect(() => {
    if (!hasChildren) {
      if (levels.length) setLevels([]);
      return;
    }
    const rootPath = getItemPath(data);
    const inThisTree = !!rootPath && isInsideTree(pathname, rootPath);

    if (!inThisTree) {
      if (levels.length) setLevels([]);
      return;
    }

    const chain = computeOpenChain(data);
    if (!chain.length) {
      if (levels.length) setLevels([]);
      return;
    }

    // Create new levels (instant mount)
    const nextLevels: LevelItem[] = chain.map((menu) => ({
      id: nextLevelId(),
      menu,
      open: true,
      mountInstant: true,
    }));

    // Avoid state update if nothing changes (compare by menu reference and open=true)
    const same =
      levels.length === nextLevels.length &&
      levels.every((l, i) => l.menu === nextLevels[i].menu && l.open === true);

    if (!same) setLevels(nextLevels);
  }, [pathname, data, hasChildren, computeOpenChain]);

  const renderNavItem = () => (
    <NavItem
      path={data.category.path}
      icon={data.category.icon}
      title={data.category.title}
      type={data.category.type}
      open={levels.some((l) => l.open)}
      active={isActive}
      hasChild={hasChildren}
      externalLink={isExternalLink(data.category.path)}
      onClick={openFirstLevel}
    />
  );

  const renderDrawerLevel = (item: LevelItem, levelIndex: number) => {
    const menu = item.menu;

    return (
      <Drawer
        key={item.id}
        open={item.open}
        onClose={() => closeFromIndex(levelIndex)}
        onExited={cleanupTrailingClosed}
        mountInstant={item.mountInstant === true}
        hideBackdrop
        anchor="right"
        className="max-w-[var(--layout-nav-mobile-width)] w-full py-1 overflow-y-auto"
        style={{ zIndex: 1300 + levelIndex }}
      >
        <Box className="pt-3 pb-2 pl-2.5 flex items-center">
          <Box
            onClick={() => closeFromIndex(levelIndex)}
            className="cursor-pointer flex items-center"
            role="button"
          >
            <Iconify icon="lucide:chevron-left" width={20} height={20} />
            <Typography variant="body2" className="ml-2 text-gray-400 uppercase">
              {t('other.back')}
            </Typography>
          </Box>
        </Box>

        {menu?.category.type !== 'folder' && (
          <Box>
            <NavItem
              subHeader
              externalLink={isExternalLink(menu.category?.path)}
              title={menu.category?.title}
              path={menu.category?.path}
              type={menu.category?.type}
            />
          </Box>
        )}

        <Box className="flex flex-col gap-2">
          <ul className="space-y-2">
            {menu?.children?.map((child: any, idx: number) => {
              const itemTitle = child.category?.title ?? child.title;
              const itemPath = child.category?.path ?? child.path;
              const itemType = child.category?.type ?? child.type;
              const itemIcon = child.category?.icon ?? child.icon;
              const hasChild = !!child.children?.length;

              return (
                <li key={idx} className="mt-2">
                  <NavItem
                    subItem
                    title={itemTitle}
                    path={itemPath}
                    type={itemType}
                    icon={itemIcon}
                    active={isActiveLink(pathname, itemPath)}
                    hasChild={hasChild}
                    externalLink={isExternalLink(itemPath)}
                    onClick={(e) => {
                      if (!hasChild) {
                        // allow navigation then close all with animation
                        closeAll();
                        return;
                      }
                      e.preventDefault();
                      e.stopPropagation();
                      pushLevel(child, levelIndex);
                    }}
                  />
                </li>
              );
            })}
          </ul>
        </Box>
      </Drawer>
    );
  };

  const anyOpen = useMemo(() => levels.some((l) => l.open), [levels]);

  return (
    <NavLi className={className} {...other}>
      {renderNavItem()}
      {levels.map((item, levelIndex) => renderDrawerLevel(item, levelIndex))}
      {!anyOpen && levels.length > 0 && null}
    </NavLi>
  );
}
